/**
 * MobileDock Component for LOOPINO AI Platform
 * Mobile navigation dock using the provided Dock component
 */

import React from 'react';
import './MobileDock.css';
import Dock from './Dock';
import useTestMode from '../../../hooks/useTestMode.js';
import useLanguage from '../../../hooks/useLanguage';
import { useCapabilities } from '../../../capabilities/CapabilityContext';

// Import icons
import iconHome from '../../../assets/images/icons/icon-home.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';
import iconAIFolder from '../../../assets/images/icons/icon-ai-folder.svg';
import iconStrategy from '../../../assets/images/icons/icon-strategy.svg';
import iconImage from '../../../assets/images/icons/icon-new-image.svg';

// Import icons - using simple SVG icons since we don't have VSCode icons
const HomeIcon = ({ size = 24 }) => (
  <img src={iconHome} alt="" aria-hidden="true" style={{ width: size, height: size, filter: 'brightness(0) invert(1)', objectFit: 'contain' }} />
);

const CalendarIcon = ({ size = 24 }) => (
  <img src={iconCalendar} alt="" aria-hidden="true" style={{ width: size, height: size, filter: 'brightness(0) invert(1)', objectFit: 'contain' }} />
);

const AIStorageIcon = ({ size = 24 }) => (
  <img src={iconAIFolder} alt="" aria-hidden="true" style={{ width: size, height: size, filter: 'brightness(0) invert(1)', objectFit: 'contain' }} />
);

const StrategyIcon = ({ size = 24 }) => (
  <img src={iconStrategy} alt="" aria-hidden="true" style={{ width: size, height: size, filter: 'brightness(0) invert(1)', objectFit: 'contain' }} />
);

const ImageGeneratorIcon = ({ size = 24 }) => (
  <img src={iconImage} alt="" aria-hidden="true" style={{ width: size, height: size, filter: 'brightness(0) invert(1)', objectFit: 'contain' }} />
);

const HOME_PRIMARY_EVENT = 'mobileDockHomePrimaryRequest';


const MobileDock = ({ 
  currentScreen = 'posts', 
  onScreenChange 
}) => {
  const { t } = useLanguage();
  const { isTestModeEnabled } = useTestMode();
  const { getNavigationStateForScreen, isAccessible } = useCapabilities();

  // Navigation handler
  const navigateToScreen = (screen, options = {}) => {
    console.log('[MobileDock] Navigating to screen:', screen);
    const { triggerPrimaryRequest = false } = options;
    if (onScreenChange) {
      onScreenChange(screen, { source: 'dock' });
    }

    if (triggerPrimaryRequest && typeof window !== 'undefined') {
      const dispatchEvent = () => {
        window.dispatchEvent(new CustomEvent(HOME_PRIMARY_EVENT, { detail: { source: 'dock' } }));
      };

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(dispatchEvent);
      } else {
        window.setTimeout(dispatchEvent, 0);
      }
    }
  };

  const baseNavItems = [
    {
      screen: 'image-generator',
      icon: <ImageGeneratorIcon size={24} />,
      label: t('mobileDock.labels.imageGenerator', { defaultValue: 'AI Images' }),
      onClick: () => navigateToScreen('image-generator'),
      className: currentScreen === 'image-generator' ? 'active' : ''
    },
    {
      screen: 'calendar',
      icon: <CalendarIcon size={24} />,
      label: t('mobileDock.labels.calendar', { defaultValue: 'Calendar' }),
      onClick: () => navigateToScreen('calendar'),
      className: currentScreen === 'calendar' ? 'active' : ''
    },
    {
      screen: 'posts',
      icon: <HomeIcon size={24} />,
      label: t('mobileDock.labels.posts', { defaultValue: 'Posts' }),
      onClick: () => navigateToScreen('posts', { triggerPrimaryRequest: currentScreen === 'posts' }),
      className: currentScreen === 'posts' ? 'active' : ''
    },
    {
      screen: 'aistorage',
      icon: <AIStorageIcon size={24} />,
      label: t('mobileDock.labels.aiStorage', { defaultValue: 'AI Storage' }),
      onClick: () => navigateToScreen('aistorage'),
      className: currentScreen === 'aistorage' ? 'active' : ''
    },
    {
      screen: 'strategy',
      icon: <StrategyIcon size={24} />,
      label: t('mobileDock.labels.strategy', { defaultValue: 'Strategy' }),
      onClick: () => navigateToScreen('strategy'),
      className: currentScreen === 'strategy' ? 'active' : ''
    }
  ];

  const developmentNavItems = [];
  const mobileNavItems = [...baseNavItems, ...(isTestModeEnabled ? developmentNavItems : [])]
    .filter((item) => {
      const capability = getNavigationStateForScreen(item.screen);
      return !capability.managed || isAccessible(capability);
    });

  return (
    <div className="mobile-dock-wrapper">
      <Dock 
        items={mobileNavItems}
        panelHeight={55}
        baseItemSize={38}
        magnification={48}
        spring={{ mass: 0.03, stiffness: 600, damping: 25 }}
        className="mobile-dock"
      />
    </div>
  );
};

export default MobileDock; 
